import { getActivityName } from './activities';

/**
 * Company size options with localized labels
 */
export function getCompanySizes(content) {
    return [
        { value: "1", label: content?.size1_10 || "less than 50 employees" },
        { value: "2", label: content?.size11_50 || "50-249 employees" },
        { value: "3", label: content?.size51_250 || "250-999 employees" },
        { value: "4", label: content?.size251_1000 || "1000-4999 employees" },
        { value: "5", label: content?.size1001Plus || "5000+ employees" }
    ]
}

/**
 * Fiscal year month options with localized labels
 */
export function getFiscalMonths(content) {
    return [
        { value: "1", label: content?.januaryLabel || "January" },
        { value: "2", label: content?.februaryLabel || "February" },
        { value: "3", label: content?.marchLabel || "March" },
        { value: "4", label: content?.aprilLabel || "April" },
        { value: "5", label: content?.mayLabel || "May" },
        { value: "6", label: content?.juneLabel || "June" },
        { value: "7", label: content?.julyLabel || "July" },
        { value: "8", label: content?.augustLabel || "August" },
        { value: "9", label: content?.septemberLabel || "September" },
        { value: "10", label: content?.octoberLabel || "October" },
        { value: "11", label: content?.novemberLabel || "November" },
        { value: "12", label: content?.decemberLabel || "December" }
    ]
}

/**
 * Registration industry options with localized labels
 * value: Represents industryId, which is how it ends up being computered by Snowflake
 * Please do not change the value without consuting the data team first!
 */
export function getIndustries(content)  {
    return [
        { value: "0", label: content?.agricultureForestryFishingHuntingLabel || "Agriculture, Forestry, Fishing & Hunting" },
        { value: "1", label: content?.miningLabel || "Mining" },
        { value: "2", label: content?.utilitiesLabel || "Utilities" },
        { value: "3", label: content?.constructionLabel || "Construction" },
        { value: "4", label: content?.manufacturingLabel || "Manufacturing" },
        { value: "5", label: content?.wholesaleRetailTradeLabel || "Wholesale & Retail Trade" },
        { value: "6", label: content?.transportationWarehousingLabel || "Transportation & Warehousing" },
        { value: "7", label: content?.businessAdminServicesLabel || "Business & Administration Services" },
        { value: "8", label: content?.wasteManagementRemediationLabel || "Waste Management & Remediation Services" },
        { value: "9", label: content?.accommodationFoodServicesLabel || "Accommodation & Food Services" },
        { value: "10", label: content?.otherServicesLabel || "Other Services" },
    ]
}

/**
 * Hotspot sector label mapping
 * Maps sector codes to their corresponding content label keys
 */
const HOTSPOT_SECTOR_LABEL_MAP = {
    'BusinessServices': 'businessServicesLabel',
    'BasicMetals': 'basicMetalsLabel',
    'ComputerElectronicAndOpticalEquipment': 'computerElectronicAndOpticalEquipmentLabel',
    'TransportationExceptRail': 'transportationExceptRailLabel',
    'FabricatedMetalProducts': 'fabricatedMetalProductsLabel',
    'Construction': 'constructionLabel',
    'RubberAndPlasticsProducts': 'rubberAndPlasticsProductsLabel',
    'MotorVehiclesAndOtherTransportEquipment': 'motorVehiclesAndOtherTransportEquipmentLabel',
    'MachineryAndOtherElectricalEquipment': 'machineryAndOtherElectricalEquipmentLabel',
    'TransportationRail': 'transportationRailLabel',
    'BasicChemicals': 'basicChemicalsLabel',
    'IndustrialGases': 'industrialGasesLabel',
    'SpecialityChemicals': 'specialityChemicalsLabel',
};

/**
 * Get translated label for a hotspot sector
 * @param {Object} content - Translated content from useContentHotspotSectors
 * @param {string} sectorCode - The sector code (e.g., 'BusinessServices')
 * @param {string} fallbackName - Fallback name if translation not found
 * @returns {string} Translated label or fallback
 */
export function getHotspotSectorLabel(content, sectorCode, fallbackName) {
    if (!content || !sectorCode) return fallbackName || sectorCode || '';

    const labelKey = HOTSPOT_SECTOR_LABEL_MAP[sectorCode];
    if (labelKey && content[labelKey]) {
        return content[labelKey];
    }

    return fallbackName || sectorCode || '';
}

/**
 * Transform hotspot sectors array with translated labels
 * Takes sectors from the API and adds translated friendlyName
 *
 * @param {Object} content - Translated content from useContentHotspotSectors
 * @param {Array} sectors - Array of hotspot sectors from API
 * @returns {Array} Sectors with translated labels
 */
export function getHotspotSectors(content, sectors = []) {
    if (!Array.isArray(sectors)) return [];

    return sectors.map(sector => {
        // Get the sector code - could be in different field names
        const sectorCode = sector.code || sector.HotspotSectorId || '';
        const originalName = sector.friendlyName || sector.FriendlyName || sector.name || sector.HotspotSectorName || '';

        // Get translated label
        const translatedLabel = getHotspotSectorLabel(content, sectorCode, originalName);

        return {
            ...sector,
            // Override friendlyName with translated version
            friendlyName: translatedLabel,
            // Keep original name for reference
            originalFriendlyName: originalName,
        };
    });
}

/**
 * Get translated label for a carbon accounting activity
 * Uses the static mapping from activities.js to find the translation
 *
 * @param {Object} content - Translated content from useContentActivityList
 * @param {string} activityId - The activity ID
 * @param {string} fallbackName - Fallback name if translation not found
 * @returns {string} Translated label or fallback
 */
export function getActivityLabel(content, activityId, fallbackName) {
    return getActivityName(content, activityId, fallbackName);
}

/**
 * Transform activities array with translated labels
 * Takes activities from the API and adds translated name
 *
 * @param {Object} content - Translated content from useContentActivityList
 * @param {Array} activities - Array of activities from API
 * @returns {Array} Activities with translated labels
 */
export function getActivities(content, activities = []) {
    if (!Array.isArray(activities)) return [];

    return activities.map(activity => {
        const activityId = activity.activityId || activity.ActivityId || '';
        const originalName = activity.name || activity.ActivityName || '';
        const translatedLabel = getActivityName(content, activityId, originalName);

        return {
            ...activity,
            name: translatedLabel,
            originalName: originalName,
        };
    });
}

/**
 * Scope 3 sector label mapping
 * Maps sector codes (from Snowflake/database) to their corresponding content keys
 * Keys are the actual sector codes from the API (e.g., "C17T18")
 * Values are the camelCase keys in the content object
 */
const SCOPE3_SECTOR_LABEL_MAP = {
    // Sector codes from database mapped to content keys
    'C01T03': 'agricultureHuntingForestryFishingAqua',    // Agriculture, Hunting, Forestry, Fishing & Aquaculture
    'C05T09': 'miningQuarryingMiningSupport',             // Mining, Quarrying & Mining Support
    'C10T12': 'foodProductsBeveragesTobacco',             // Food Products, Beverages & Tobacco
    'C13T15': 'textilesLeatherFootwear',                  // Textiles, Leather & Footwear
    'C16T16': 'woodProductCorkExceptFurniture',           // Wood Products & Cork (except Furniture)
    'C17T18': 'paperPrinting',                            // Paper Products & Printing
    'C19T19': 'cokeRefinedPetrolum',                      // Coke & Refined Petroleum Products
    'C20T20_1': 'basicChemicalsExcludingIndustrialGases', // Basic Chemicals (excl. Industrial Gases)
    'C20T20_2': 'industrialGases',                        // Industrial Gases
    'C20T20_3': 'specialtyChemicals',                     // Specialty Chemicals
    'C21T21': 'pharmaMedicinalChemicalBotanicalProducts', // Pharma, Medicinal, Chemical & Botanical Products
    'C22T22': 'rubberPlasticProducts',                    // Rubber & Plastic Products
    'C23T23': 'nonMetallicMineralProducts',               // Non-Metallic Mineral Products
    'C24T24': 'basicMetals',                              // Basic Metals
    'C25T25': 'fabricatedMetalProducts',                  // Fabricated Metal Products
    'C26T26': 'computerElectronicOpticalEquipment',       // Computer, Electronic & Optical Equipment
    'C27T28': 'machineryOtherElectricalEquipment',        // Machinery & Other Electrical Equipment
    'C29T30': 'motorVehiclesTrailersSemiTrailersOther',   // Motor Vehicles, Trailers, Semi-trailers & Other
    'C31T33': 'otherMiscManufacturing',                   // Other Misc. Manufacturing
    'N2213A0': 'waterSupplyIrrigation',                   // Water Supply & Irrigation
    'N562000': 'wasteManagementRemediation',              // Waste Management & Remediation
    'C41T43': 'constructionServices',                     // Construction Services
    'N481C00': 'airTransport',                            // Air Transport
    'N483000': 'waterTransport',                          // Water Transport
    'N484000': 'truckTransport',                          // Truck Transport
    'N482110': 'railTransport',                           // Rail Transport
    'N48C000': 'transitGroundPassenger',                  // Transit & Ground Passenger
    'C52T52': 'warehousingSupport',                       // Warehousing & Support
    'C53T53': 'postalCourier',                            // Postal & Courier
    'C55T56': 'accomodationFoodServices',                 // Accommodation & Food Services
    'C61T61_C77T82': 'businessServices',                  // Business Services
    'C68T68': 'realEstate',                               // Real Estate
};

/**
 * Convert Scope 3 sector code to content key
 * Looks up the sector code directly in the mapping
 *
 * @param {string} sectorCode - Sector code from API (e.g., "C17T18")
 * @returns {string|null} Content key or null if not found
 */
function scope3SectorCodeToContentKey(sectorCode) {
    if (!sectorCode) return null;

    // Direct lookup - codes are exact matches like "C17T18"
    if (SCOPE3_SECTOR_LABEL_MAP[sectorCode]) {
        return SCOPE3_SECTOR_LABEL_MAP[sectorCode];
    }

    // Try uppercase version in case it comes in lowercase
    const upperCode = sectorCode.toUpperCase();
    if (SCOPE3_SECTOR_LABEL_MAP[upperCode]) {
        return SCOPE3_SECTOR_LABEL_MAP[upperCode];
    }

    return null;
}

/**
 * Get translated label for a Scope 3 sector (product/service)
 * @param {Object} content - Translated content from useContentScope3SectorList
 * @param {string} sectorCode - The sector code (e.g., 'business_services')
 * @param {string} fallbackName - Fallback name if translation not found
 * @returns {string} Translated label or fallback
 */
export function getScope3SectorLabel(content, sectorCode, fallbackName) {
    if (!content || !sectorCode) return fallbackName || sectorCode || '';

    const contentKey = scope3SectorCodeToContentKey(sectorCode);
    if (contentKey && content[contentKey]) {
        return content[contentKey];
    }

    return fallbackName || sectorCode || '';
}

/**
 * Transform Scope 3 sectors array with translated labels
 * Takes sectors from the API and adds translated name
 *
 * @param {Object} content - Translated content from useContentScope3SectorList
 * @param {Array} sectors - Array of Scope 3 sectors from API
 * @returns {Array} Sectors with translated labels
 */
export function getScope3Sectors(content, sectors = []) {
    if (!Array.isArray(sectors)) return [];

    return sectors.map(sector => {
        const sectorCode = sector.code || sector.Scope3SectorCode || '';
        const originalName = sector.name || sector.Scope3SectorName || '';
        const translatedLabel = getScope3SectorLabel(content, sectorCode, originalName);

        return {
            ...sector,
            name: translatedLabel,
            originalName: originalName,
        };
    });
}